import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const INITIAL_EVENT = {
  team_id: '',
  jogador_id: '',
  assist_jogador_id: '',
  event_type: 'goal',
  minute: '',
  description: '',
};

const OFFICIATING_ROLES = [
  { code: 'head_referee', label: 'Árbitro principal' },
  { code: 'assistant_referee_1', label: 'Assistente 1' },
  { code: 'assistant_referee_2', label: 'Assistente 2' },
  { code: 'fourth_official', label: 'Quarto árbitro' },
  { code: 'var', label: 'VAR' },
  { code: 'avar', label: 'AVAR' },
];

const EVENT_LABELS = {
  goal: 'Gol',
  penalty_scored: 'Gol de pênalti',
  own_goal: 'Gol contra',
  yellow_card: 'Cartão amarelo',
  red_card: 'Cartão vermelho',
  substitution: 'Substituição',
  corner: 'Escanteio',
  shot_on_target: 'Finalização no alvo',
  shot_off_target: 'Finalização para fora',
  foul_suffered: 'Falta sofrida',
  foul_committed: 'Falta cometida',
  interception: 'Interceptação',
};

function eventLabel(type) {
  return EVENT_LABELS[type] || 'Lance';
}

const EMPTY_FIELD_FORM = {
  name: '',
  address: '',
  city: '',
  state: '',
  turf_type: 'synthetic',
  capacity: '',
  has_lighting: true,
  has_parking: false,
  photo_url: '',
};

const EMPTY_MATCH_FORM = {
  campeonato_id: '',
  phase: '',
  round_number: '',
  group_name: '',
  campo_id: '',
  home_team_id: '',
  away_team_id: '',
  scheduled_at: '',
  status: 'scheduled',
  referee_user_id: '',
  referee_name: '',
  notes: '',
  transmission_provider: 'youtube',
  transmission_title: '',
  transmission_stream_url: '',
  transmission_embed_url: '',
  transmission_youtube_video_id: '',
  transmission_replay_url: '',
  transmission_thumbnail_url: '',
  transmission_is_live: true,
  officiating_team: OFFICIATING_ROLES.map((role) => ({
    role_code: role.code,
    user_id: '',
    display_name: '',
    notes: '',
  })),
};

const FORMATION_OPTIONS = ['4-3-3', '4-4-2', '4-2-3-1', '3-5-2'];

function toDateTimeLocal(value) {
  if (!value) return '';
  return value.replace(' ', 'T').slice(0, 16);
}

function createLineupState(selectedMatch, allPlayers) {
  if (!selectedMatch) {
    return {};
  }

  const teamIds = [selectedMatch.home_team_id, selectedMatch.away_team_id].filter(Boolean);
  const state = {};

  teamIds.forEach((teamId) => {
    const existing = (selectedMatch.lineups || []).find((lineup) => Number(lineup.time_id) === Number(teamId));
    const roster = allPlayers
      .filter((player) => Number(player.time_id) === Number(teamId))
      .map((player) => {
        const lineupPlayer = (existing?.players || []).find((item) => Number(item.jogador_id) === Number(player.id));
        return {
          jogador_id: player.id,
          name: player.full_name,
          nickname: player.nickname,
          position: player.position,
          number: player.number,
          photo_url: player.photo_url,
          is_starter: Boolean(lineupPlayer?.is_starter),
          captain: Boolean(lineupPlayer?.captain),
          shirt_number: lineupPlayer?.shirt_number ?? player.number ?? '',
        };
      });

    state[teamId] = {
      team_id: teamId,
      formation: existing?.formation || '4-3-3',
      status: existing?.status === 'locked' ? 'confirmed' : (existing?.status || 'draft'),
      players: roster,
    };
  });

  return state;
}

function LineupEditor({ title, lineup, disabled, onChange }) {
  if (!lineup) {
    return null;
  }

  const starters = lineup.players.filter((player) => player.is_starter);
  const bench = lineup.players.filter((player) => !player.is_starter);

  return (
    <SectionCard title={title} subtitle="Defina titulares, reservas e capitão do time.">
      <div className="detail-stack compact">
        <div className="form-row">
          <label>
            Formação
            <select value={lineup.formation} onChange={(event) => onChange({ ...lineup, formation: event.target.value })} disabled={disabled}>
              {FORMATION_OPTIONS.map((formation) => <option key={formation} value={formation}>{formation}</option>)}
            </select>
          </label>
          <label>
            Status
            <select value={lineup.status} onChange={(event) => onChange({ ...lineup, status: event.target.value })} disabled={disabled}>
              <option value="draft">Rascunho</option>
              <option value="confirmed">Confirmada</option>
            </select>
          </label>
        </div>

        <div className="lineup-columns">
          <div className="lineup-column">
            <strong>Titulares</strong>
            <div className="lineup-player-list">
              {starters.map((player) => (
                <div key={player.jogador_id} className="lineup-player-card">
                  <div>
                    <span className="lineup-player-name">{player.name}</span>
                    <span className="table-muted">{player.position} #{player.number || '-'}</span>
                  </div>
                  <div className="inline-actions">
                    <label className="mini-toggle">
                      <input
                        type="checkbox"
                        checked={player.captain}
                        disabled={disabled}
                        onChange={(event) => {
                          onChange({
                            ...lineup,
                            players: lineup.players.map((item) => ({
                              ...item,
                              captain: item.jogador_id === player.jogador_id ? event.target.checked : false,
                            })),
                          });
                        }}
                      />
                      Capitão
                    </label>
                    <button
                      className="inline-button"
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        onChange({
                          ...lineup,
                          players: lineup.players.map((item) => item.jogador_id === player.jogador_id ? { ...item, is_starter: false, captain: false } : item),
                        });
                      }}
                    >
                      Virar reserva
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lineup-column">
            <strong>Reservas</strong>
            <div className="lineup-player-list">
              {bench.map((player) => (
                <div key={player.jogador_id} className="lineup-player-card">
                  <div>
                    <span className="lineup-player-name">{player.name}</span>
                    <span className="table-muted">{player.position} #{player.number || '-'}</span>
                  </div>
                  <button
                    className="inline-button"
                    type="button"
                    disabled={disabled || starters.length >= 11}
                    onClick={() => {
                      onChange({
                        ...lineup,
                        players: lineup.players.map((item) => item.jogador_id === player.jogador_id ? { ...item, is_starter: true } : item),
                      });
                    }}
                    >
                      Virar titular
                    </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SectionCard>
  );
}

export function MatchesPage({
  loading,
  error,
  data,
  selectedMatch,
  allPlayers,
  onSelectMatch,
  onCreateMatch,
  onUpdateMatch,
  onProcessReminders,
  onDeleteMatch,
  onStartMatch,
  onFinishMatch,
  onUpdateScore,
  onRegisterEvent,
  onSaveLineups,
  onCreateField,
  onUpdateField,
  onDeleteField,
  matchBusy,
  canManageSchedule,
  canManageLineups,
}) {
  const rows = data?.items || [];
  const [scoreForm, setScoreForm] = useState({ home_score: '0', away_score: '0' });
  const [eventForm, setEventForm] = useState(INITIAL_EVENT);
  const [finishNotes, setFinishNotes] = useState('');
  const [matchForm, setMatchForm] = useState(EMPTY_MATCH_FORM);
  const [fieldForm, setFieldForm] = useState(EMPTY_FIELD_FORM);
  const [editingFieldId, setEditingFieldId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [lineupsState, setLineupsState] = useState({});
  const championshipTeamLinks = data?.championshipTeams || [];

  const clearTransmissionForm = () => {
    setMatchForm((current) => ({
      ...current,
      transmission_provider: 'youtube',
      transmission_title: '',
      transmission_stream_url: '',
      transmission_embed_url: '',
      transmission_youtube_video_id: '',
      transmission_replay_url: '',
      transmission_thumbnail_url: '',
      transmission_is_live: true,
    }));
  };

  useEffect(() => {
    if (selectedMatch) {
      setScoreForm({
        home_score: String(selectedMatch.home_score ?? 0),
        away_score: String(selectedMatch.away_score ?? 0),
      });
      setEventForm({
        ...INITIAL_EVENT,
        team_id: String(selectedMatch.home_team_id ?? ''),
      });
      setFinishNotes(selectedMatch.notes || '');
      setEditingId(selectedMatch.id);
      const transmission = selectedMatch.transmission || {};
      setMatchForm({
        campeonato_id: String(selectedMatch.campeonato_id || ''),
        phase: selectedMatch.phase || '',
        round_number: selectedMatch.round_number ? String(selectedMatch.round_number) : '',
        group_name: selectedMatch.group_name || '',
        campo_id: selectedMatch.campo_id ? String(selectedMatch.campo_id) : '',
        home_team_id: String(selectedMatch.home_team_id || ''),
        away_team_id: String(selectedMatch.away_team_id || ''),
        scheduled_at: toDateTimeLocal(selectedMatch.scheduled_at),
        status: selectedMatch.status || 'scheduled',
        referee_user_id: '',
        referee_name: selectedMatch.referee_name || '',
        notes: selectedMatch.notes || '',
        transmission_provider: transmission.provider || 'youtube',
        transmission_title: transmission.title || '',
        transmission_stream_url: transmission.stream_url || '',
        transmission_embed_url: transmission.embed_url || '',
        transmission_youtube_video_id: transmission.youtube_video_id || '',
        transmission_replay_url: transmission.replay_url || '',
        transmission_thumbnail_url: transmission.thumbnail_url || '',
        transmission_is_live: Boolean(transmission.is_live),
        officiating_team: OFFICIATING_ROLES.map((role) => {
          const existing = (selectedMatch.officiating_team || []).find((item) => item.role_code === role.code);
          return {
            role_code: role.code,
            user_id: existing?.user_id ? String(existing.user_id) : '',
            display_name: existing?.display_name || '',
            notes: existing?.notes || '',
          };
        }),
      });
      setLineupsState(createLineupState(selectedMatch, allPlayers));
    } else {
      setEditingId(null);
      setMatchForm(EMPTY_MATCH_FORM);
      setLineupsState({});
    }
  }, [selectedMatch, allPlayers]);

  const teamOptions = useMemo(() => {
    if (!selectedMatch) return [];
    return [
      { id: selectedMatch.home_team_id, name: selectedMatch.home_team_name },
      { id: selectedMatch.away_team_id, name: selectedMatch.away_team_name },
    ];
  }, [selectedMatch]);

  const filteredPlayers = useMemo(() => {
    const source = (selectedMatch?.lineups || []).flatMap((lineup) =>
      (lineup.players || []).map((player) => ({
        jogador_id: player.jogador_id,
        jogador_name: player.jogador_name,
        team_id: lineup.time_id,
      })),
    );

    const fallback = (allPlayers || []).map((player) => ({
      jogador_id: player.id,
      jogador_name: player.full_name,
      team_id: player.time_id,
    }));

    return (source.length ? source : fallback).filter((player) => Number(player.team_id) === Number(eventForm.team_id));
  }, [allPlayers, selectedMatch, eventForm.team_id]);

  const currentChampionshipTeams = useMemo(() => {
    const championshipId = Number(matchForm.campeonato_id);
    if (!championshipId) {
      return [];
    }

    const allowedTeamIds = championshipTeamLinks
      .filter((item) => Number(item.campeonato_id) === championshipId)
      .map((item) => Number(item.time_id));

    return (data?.teams || []).filter((team) => allowedTeamIds.includes(Number(team.id)));
  }, [championshipTeamLinks, data?.teams, matchForm.campeonato_id]);

  const lineupWarnings = useMemo(() => {
    if (!selectedMatch) return [];

    return Object.values(lineupsState).flatMap((lineup) => {
      const starters = lineup.players.filter((player) => player.is_starter);
      const captains = lineup.players.filter((player) => player.captain);
      const invalidStatus = starters.filter((player) => ['injured', 'suspended'].includes(
        (allPlayers.find((item) => Number(item.id) === Number(player.jogador_id))?.status_fisico || '').toString(),
      ));
      const issues = [];

      if (lineup.status === 'confirmed' && starters.length !== 11) {
        issues.push(`${selectedMatch.home_team_id === lineup.team_id ? selectedMatch.home_team_name : selectedMatch.away_team_name}: confirmação exige 11 titulares.`);
      }
      if (lineup.status === 'confirmed' && captains.length !== 1) {
        issues.push(`${selectedMatch.home_team_id === lineup.team_id ? selectedMatch.home_team_name : selectedMatch.away_team_name}: confirmação exige 1 capitão.`);
      }
      if (invalidStatus.length) {
        issues.push(`${selectedMatch.home_team_id === lineup.team_id ? selectedMatch.home_team_name : selectedMatch.away_team_name}: há jogador lesionado/suspenso entre os titulares.`);
      }

      return issues;
    });
  }, [allPlayers, lineupsState, selectedMatch]);

  if (loading) return <LoadingBlock label="Carregando jogos..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid">
      <SectionCard
        title="Cadastro de jogos"
        subtitle="Agende a partida, defina campo, árbitro e transmissão."
        actions={canManageSchedule ? (
          <div className="inline-actions">
            <button className="ghost-button" type="button" onClick={() => { setEditingId(null); setMatchForm(EMPTY_MATCH_FORM); }}>Novo jogo</button>
            <button className="ghost-button" type="button" onClick={clearTransmissionForm}>Limpar transmissão</button>
            <button
              className="ghost-button"
              type="button"
              onClick={onProcessReminders}
              disabled={matchBusy === 'match-reminders'}
            >
              {matchBusy === 'match-reminders' ? 'Processando...' : 'Processar lembretes'}
            </button>
          </div>
        ) : null}
      >
        {canManageSchedule ? (
          <form
            className="admin-form"
            onSubmit={(event) => {
              event.preventDefault();
              const payload = {
                campeonato_id: Number(matchForm.campeonato_id),
                phase: matchForm.phase || null,
                round_number: matchForm.round_number ? Number(matchForm.round_number) : null,
                group_name: matchForm.group_name || null,
                campo_id: matchForm.campo_id ? Number(matchForm.campo_id) : null,
                home_team_id: Number(matchForm.home_team_id),
                away_team_id: Number(matchForm.away_team_id),
                scheduled_at: matchForm.scheduled_at.replace('T', ' ') + ':00',
                status: matchForm.status,
                referee_user_id: matchForm.referee_user_id ? Number(matchForm.referee_user_id) : null,
                referee_name: matchForm.referee_name || null,
                notes: matchForm.notes || null,
                transmission: matchForm.transmission_title && matchForm.transmission_stream_url ? {
                  provider: matchForm.transmission_provider,
                  title: matchForm.transmission_title,
                  stream_url: matchForm.transmission_stream_url,
                  embed_url: matchForm.transmission_embed_url || null,
                  youtube_video_id: matchForm.transmission_youtube_video_id || null,
                  replay_url: matchForm.transmission_replay_url || null,
                  thumbnail_url: matchForm.transmission_thumbnail_url || null,
                  is_live: matchForm.transmission_is_live,
                } : null,
                officiating_team: (matchForm.officiating_team || []).filter((item) => item.user_id || item.display_name),
              };

              if (editingId) {
                onUpdateMatch(editingId, payload);
              } else {
                onCreateMatch(payload);
              }
            }}
          >
            <div className="callout-box compact-box">
              <span className="eyebrow">Acesso do administrador</span>
              <strong>Controle total de jogos e vídeos</strong>
              <p>Como admin, você adiciona, edita e exclui jogos, define arbitragem, campo e configura os links da transmissão ao vivo e do replay.</p>
            </div>
            <div className="form-row triple">
              <label>
                Campeonato
                <select
                  value={matchForm.campeonato_id}
                  onChange={(event) => setMatchForm({
                    ...matchForm,
                    campeonato_id: event.target.value,
                    home_team_id: '',
                    away_team_id: '',
                  })}
                  required
                >
                  <option value="">Selecione</option>
                  {(data?.championships || []).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                </select>
              </label>
              <label>
                Rodada
                <input value={matchForm.round_number} onChange={(event) => setMatchForm({ ...matchForm, round_number: event.target.value })} />
              </label>
              <label>
                Fase
                <input value={matchForm.phase} onChange={(event) => setMatchForm({ ...matchForm, phase: event.target.value })} placeholder="Grupo, semifinal, final..." />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Mandante
                <select value={matchForm.home_team_id} onChange={(event) => setMatchForm({ ...matchForm, home_team_id: event.target.value })} required>
                  <option value="">Selecione</option>
                  {currentChampionshipTeams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
              </label>
              <label>
                Visitante
                <select value={matchForm.away_team_id} onChange={(event) => setMatchForm({ ...matchForm, away_team_id: event.target.value })} required>
                  <option value="">Selecione</option>
                  {currentChampionshipTeams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}
                </select>
              </label>
              <label>
                Grupo
                <input value={matchForm.group_name} onChange={(event) => setMatchForm({ ...matchForm, group_name: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Data e hora
                <input type="datetime-local" value={matchForm.scheduled_at} onChange={(event) => setMatchForm({ ...matchForm, scheduled_at: event.target.value })} required />
              </label>
              <label>
                Campo
                <select value={matchForm.campo_id} onChange={(event) => setMatchForm({ ...matchForm, campo_id: event.target.value })}>
                  <option value="">Não definir</option>
                  {(data?.fields || []).map((field) => <option key={field.id} value={field.id}>{field.name} - {field.city}/{field.state}</option>)}
                </select>
              </label>
              <label>
                Status
                <select value={matchForm.status} onChange={(event) => setMatchForm({ ...matchForm, status: event.target.value })}>
                  <option value="scheduled">Agendado</option>
                  <option value="paused">Pausado</option>
                  <option value="postponed">Adiado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </label>
            </div>
            <div className="form-row">
              <label>
                Árbitro
                <select value={matchForm.referee_user_id} onChange={(event) => setMatchForm({ ...matchForm, referee_user_id: event.target.value, referee_name: '' })}>
                  <option value="">Selecionar por usuário</option>
                  {(data?.referees || []).map((referee) => <option key={referee.id} value={referee.id}>{referee.name}</option>)}
                </select>
              </label>
              <label>
                Nome manual do árbitro
                <input value={matchForm.referee_name} onChange={(event) => setMatchForm({ ...matchForm, referee_name: event.target.value, referee_user_id: '' })} />
              </label>
            </div>
            <div className="detail-stack compact">
              <strong>Equipe de arbitragem</strong>
              {(matchForm.officiating_team || OFFICIATING_ROLES.map((role) => ({
                role_code: role.code,
                user_id: '',
                display_name: '',
                notes: '',
              }))).map((official, index) => {
                const role = OFFICIATING_ROLES.find((item) => item.code === official.role_code);
                return (
                  <div key={official.role_code} className="form-row triple">
                    <label>
                      {role?.label || official.role_code}
                      <select
                        value={official.user_id}
                        onChange={(event) => setMatchForm({
                          ...matchForm,
                          officiating_team: matchForm.officiating_team.map((item, itemIndex) => itemIndex === index ? { ...item, user_id: event.target.value } : item),
                        })}
                      >
                        <option value="">Nome manual</option>
                        {(data?.referees || []).map((referee) => <option key={referee.id} value={referee.id}>{referee.name}</option>)}
                      </select>
                    </label>
                    <label>
                      Nome exibido
                      <input
                        value={official.display_name}
                        onChange={(event) => setMatchForm({
                          ...matchForm,
                          officiating_team: matchForm.officiating_team.map((item, itemIndex) => itemIndex === index ? { ...item, display_name: event.target.value } : item),
                        })}
                      />
                    </label>
                    <label>
                      Observação
                      <input
                        value={official.notes}
                        onChange={(event) => setMatchForm({
                          ...matchForm,
                          officiating_team: matchForm.officiating_team.map((item, itemIndex) => itemIndex === index ? { ...item, notes: event.target.value } : item),
                        })}
                      />
                    </label>
                  </div>
                );
              })}
            </div>
            <label>
              Observações
              <textarea rows="3" value={matchForm.notes} onChange={(event) => setMatchForm({ ...matchForm, notes: event.target.value })} />
            </label>
            <div className="callout-box compact-box">
              <span className="eyebrow">Transmissão</span>
              <strong>Área de vídeos da partida</strong>
              <p>Cadastre aqui o link principal do ao vivo, o embed, o replay e a miniatura. Use “Limpar transmissão” para remover toda a configuração de vídeo.</p>
            </div>
            <div className="form-row triple">
              <label>
                Provedor
                <select value={matchForm.transmission_provider} onChange={(event) => setMatchForm({ ...matchForm, transmission_provider: event.target.value })}>
                  <option value="youtube">YouTube</option>
                  <option value="external">Externo</option>
                  <option value="upload">Upload</option>
                </select>
              </label>
              <label>
                Título da transmissão
                <input value={matchForm.transmission_title} onChange={(event) => setMatchForm({ ...matchForm, transmission_title: event.target.value })} />
              </label>
              <label>
                URL principal
                <input value={matchForm.transmission_stream_url} onChange={(event) => setMatchForm({ ...matchForm, transmission_stream_url: event.target.value })} />
              </label>
            </div>
            <div className="form-row triple">
              <label>
                Embed URL
                <input value={matchForm.transmission_embed_url} onChange={(event) => setMatchForm({ ...matchForm, transmission_embed_url: event.target.value })} />
              </label>
              <label>
                Replay URL
                <input value={matchForm.transmission_replay_url} onChange={(event) => setMatchForm({ ...matchForm, transmission_replay_url: event.target.value })} />
              </label>
              <label>
                Thumbnail URL
                <input value={matchForm.transmission_thumbnail_url} onChange={(event) => setMatchForm({ ...matchForm, transmission_thumbnail_url: event.target.value })} />
              </label>
            </div>
            <label className="checkbox-field">
              <span>Transmissão ao vivo</span>
              <input type="checkbox" checked={Boolean(matchForm.transmission_is_live)} onChange={(event) => setMatchForm({ ...matchForm, transmission_is_live: event.target.checked })} />
            </label>
            <div className="inline-actions">
              <button className="primary-button" type="submit" disabled={matchBusy === 'match-create' || matchBusy === 'match-update'}>
                {editingId ? (matchBusy === 'match-update' ? 'Salvando...' : 'Atualizar jogo') : (matchBusy === 'match-create' ? 'Criando...' : 'Criar jogo')}
              </button>
              {editingId ? (
                <button className="inline-button danger-text" type="button" onClick={() => onDeleteMatch(editingId)} disabled={matchBusy === 'match-delete'}>
                  Remover jogo
                </button>
              ) : null}
            </div>
          </form>
        ) : (
          <div className="empty-state">Seu perfil não pode agendar ou editar jogos.</div>
        )}
      </SectionCard>

      <div className="page-grid two-columns">
        <SectionCard
          title="Campos e operação"
          subtitle="CRUD de campos para agenda oficial do campeonato."
          actions={canManageSchedule ? (
            <div className="inline-actions">
              <button className="ghost-button" type="button" onClick={() => { setEditingFieldId(null); setFieldForm(EMPTY_FIELD_FORM); }}>Novo campo</button>
              {editingFieldId ? (
                <button className="ghost-button" type="button" onClick={() => { setEditingFieldId(null); setFieldForm(EMPTY_FIELD_FORM); }}>Cancelar edição</button>
              ) : null}
            </div>
          ) : null}
        >
          <div className="callout-box compact-box admin-callout">
            <span className="eyebrow">Acesso do administrador</span>
            <strong>Controle total de campos e estrutura da rodada</strong>
            <p>Como admin, você adiciona, edita e remove campos oficiais usados na agenda. Isso define local, infraestrutura e operação do jogo.</p>
          </div>
          {canManageSchedule ? (
            <form
              className="admin-form"
              onSubmit={(event) => {
                event.preventDefault();
                const payload = { ...fieldForm };
                if (editingFieldId) {
                  onUpdateField(editingFieldId, payload).then?.(() => {
                    setFieldForm(EMPTY_FIELD_FORM);
                    setEditingFieldId(null);
                  });
                  return;
                }
                onCreateField(payload).then?.(() => setFieldForm(EMPTY_FIELD_FORM));
              }}
            >
              <div className="form-row">
                <label>
                  Nome do campo
                  <input value={fieldForm.name} onChange={(event) => setFieldForm({ ...fieldForm, name: event.target.value })} required />
                </label>
                <label>
                  Endereço
                  <input value={fieldForm.address} onChange={(event) => setFieldForm({ ...fieldForm, address: event.target.value })} required />
                </label>
              </div>
              <div className="form-row triple">
                <label>
                  Cidade
                  <input value={fieldForm.city} onChange={(event) => setFieldForm({ ...fieldForm, city: event.target.value })} required />
                </label>
                <label>
                  UF
                  <input value={fieldForm.state} maxLength="2" onChange={(event) => setFieldForm({ ...fieldForm, state: event.target.value.toUpperCase() })} required />
                </label>
                <label>
                  Piso
                  <select value={fieldForm.turf_type} onChange={(event) => setFieldForm({ ...fieldForm, turf_type: event.target.value })}>
                    <option value="natural">Natural</option>
                    <option value="synthetic">Sintético</option>
                    <option value="indoor">Indoor</option>
                  </select>
                </label>
              </div>
              <div className="form-row triple">
                <label>
                  Capacidade
                  <input type="number" value={fieldForm.capacity} onChange={(event) => setFieldForm({ ...fieldForm, capacity: event.target.value })} />
                </label>
                <label className="checkbox-field">
                  <span>Iluminação</span>
                  <input type="checkbox" checked={Boolean(fieldForm.has_lighting)} onChange={(event) => setFieldForm({ ...fieldForm, has_lighting: event.target.checked })} />
                </label>
                <label className="checkbox-field">
                  <span>Estacionamento</span>
                  <input type="checkbox" checked={Boolean(fieldForm.has_parking)} onChange={(event) => setFieldForm({ ...fieldForm, has_parking: event.target.checked })} />
                </label>
              </div>
              <label>
                Foto do campo
                <input value={fieldForm.photo_url} onChange={(event) => setFieldForm({ ...fieldForm, photo_url: event.target.value })} />
              </label>
              <div className="inline-actions">
                <button className="primary-button" type="submit" disabled={matchBusy === 'field-create' || matchBusy === 'field-update'}>
                  {editingFieldId ? 'Atualizar campo' : 'Criar campo'}
                </button>
                {editingFieldId ? (
                  <button className="ghost-button" type="button" onClick={() => { setEditingFieldId(null); setFieldForm(EMPTY_FIELD_FORM); }}>
                    Cancelar
                  </button>
                ) : null}
              </div>
            </form>
          ) : (
            <div className="empty-state">Seu perfil não pode gerenciar campos.</div>
          )}

          <div className="timeline-list">
            {(data?.fields || []).map((field) => (
              <article key={field.id} className="timeline-item">
                <div>
                  <strong>{field.name}</strong>
                  <p>{field.city}/{field.state} • {field.turf_type}</p>
                </div>
                {canManageSchedule ? (
                  <div className="inline-actions">
                    <button className="inline-button" type="button" onClick={() => {
                      setEditingFieldId(field.id);
                      setFieldForm({
                        name: field.name || '',
                        address: field.address || '',
                        city: field.city || '',
                        state: field.state || '',
                        turf_type: field.turf_type || 'synthetic',
                        capacity: field.capacity || '',
                        has_lighting: Boolean(field.has_lighting),
                        has_parking: Boolean(field.has_parking),
                        photo_url: field.photo_url || '',
                      });
                    }}>
                      Editar
                    </button>
                    <button className="inline-button danger-text" type="button" onClick={() => onDeleteField(field.id)}>
                      Remover
                    </button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </SectionCard>

        <SectionCard title="Agenda de jogos" subtitle="Fila principal de operação e transmissão.">
          <div className="callout-box compact-box admin-callout subtle-callout">
            <span className="eyebrow">Uso do administrador</span>
            <strong>Selecione uma partida para operar, editar ou remover</strong>
            <p>A agenda centraliza o acesso rápido para alterar jogo, revisar arbitragem, ajustar vídeos e entrar na central operacional.</p>
          </div>
          <DataTable
            rows={rows}
            columns={[
              { key: 'campeonato_name', label: 'Campeonato' },
              { key: 'scheduled_at', label: 'Data' },
              { key: 'home_team_name', label: 'Mandante' },
              { key: 'away_team_name', label: 'Visitante' },
              { key: 'campo_name', label: 'Campo' },
              { key: 'status', label: 'Status', type: 'status' },
              {
                key: 'actions',
                label: 'Ação',
                render: (row) => (
                  <button className="inline-button" type="button" onClick={() => onSelectMatch(row.id)}>
                    Operar
                  </button>
                ),
              },
            ]}
          />
        </SectionCard>

        <SectionCard title="Central da partida" subtitle="Operação em tempo real, placar, eventos e fechamento técnico.">
          {selectedMatch ? (
            <div className="detail-stack">
              <div className="score-strip">
                <div>
                  <span className="eyebrow">Mandante</span>
                  <strong>{selectedMatch.home_team_name}</strong>
                </div>
                <div className="score-strip__score">{selectedMatch.home_score} x {selectedMatch.away_score}</div>
                <div>
                  <span className="eyebrow">Visitante</span>
                  <strong>{selectedMatch.away_team_name}</strong>
                </div>
              </div>

              <div className="callout-box compact-box admin-callout subtle-callout">
                <span className="eyebrow">Central operacional</span>
                <strong>Comando técnico da partida</strong>
                <p>Como admin, você controla placar, timeline, escalações, arbitragem e fechamento técnico da rodada a partir daqui.</p>
              </div>

              <div className="callout-grid">
                <div className="callout-box compact-box">
                  <span className="eyebrow">Status</span>
                  <strong>{selectedMatch.status}</strong>
                </div>
                <div className="callout-box compact-box">
                  <span className="eyebrow">Campo</span>
                  <strong>{selectedMatch.campo_name || '-'}</strong>
                </div>
                <div className="callout-box compact-box">
                  <span className="eyebrow">Árbitro</span>
                  <strong>{selectedMatch.referee_name || '-'}</strong>
                </div>
              </div>

              {canManageLineups ? (
                <div className="page-grid two-columns">
                  <LineupEditor
                    title={`Escalação • ${selectedMatch.home_team_name}`}
                    lineup={lineupsState[selectedMatch.home_team_id]}
                    disabled={matchBusy === 'lineups'}
                    onChange={(next) => setLineupsState((current) => ({ ...current, [selectedMatch.home_team_id]: next }))}
                  />
                  <LineupEditor
                    title={`Escalação • ${selectedMatch.away_team_name}`}
                    lineup={lineupsState[selectedMatch.away_team_id]}
                    disabled={matchBusy === 'lineups'}
                    onChange={(next) => setLineupsState((current) => ({ ...current, [selectedMatch.away_team_id]: next }))}
                  />
                </div>
              ) : null}

              {canManageLineups ? (
                <>
                  {lineupWarnings.length ? (
                    <div className="page-error small">
                      {lineupWarnings.join(' ')}
                    </div>
                  ) : null}
                </>
              ) : null}

              {canManageLineups ? (
                <button
                  className="primary-button"
                  type="button"
                  disabled={matchBusy === 'lineups' || lineupWarnings.length > 0}
                  onClick={() => {
                    onSaveLineups(selectedMatch.id, {
                      lineups: Object.values(lineupsState).map((lineup) => ({
                        time_id: lineup.team_id,
                        formation: lineup.formation,
                        status: lineup.status,
                        players: lineup.players.map((player) => ({
                          jogador_id: player.jogador_id,
                          is_starter: player.is_starter,
                          captain: player.captain,
                          shirt_number: player.shirt_number,
                        })),
                      })),
                    });
                  }}
                >
                  {matchBusy === 'lineups' ? 'Salvando escalações...' : 'Salvar escalações'}
                </button>
              ) : null}

              <div className="page-grid two-columns match-ops-grid">
                <form
                  className="admin-form"
                  onSubmit={(event) => {
                    event.preventDefault();
                    onUpdateScore(selectedMatch.id, {
                      home_score: Number(scoreForm.home_score),
                      away_score: Number(scoreForm.away_score),
                    });
                  }}
                >
                  <h3>Placar operacional</h3>
                  <div className="form-row">
                    <label>
                      {selectedMatch.home_team_short_name || 'Casa'}
                      <input type="number" min="0" value={scoreForm.home_score} onChange={(e) => setScoreForm({ ...scoreForm, home_score: e.target.value })} />
                    </label>
                    <label>
                      {selectedMatch.away_team_short_name || 'Fora'}
                      <input type="number" min="0" value={scoreForm.away_score} onChange={(e) => setScoreForm({ ...scoreForm, away_score: e.target.value })} />
                    </label>
                  </div>
                  <button className="primary-button" type="submit" disabled={matchBusy === 'score'}>
                    {matchBusy === 'score' ? 'Salvando...' : 'Salvar placar'}
                  </button>
                </form>

                <div className="match-actions">
                  <button className="primary-button" type="button" onClick={() => onStartMatch(selectedMatch.id)} disabled={matchBusy === 'start' || selectedMatch.status === 'live' || selectedMatch.status === 'finished'}>
                    {matchBusy === 'start' ? 'Iniciando...' : 'Iniciar partida'}
                  </button>
                  <form
                    className="admin-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      onFinishMatch(selectedMatch.id, { notes: finishNotes });
                    }}
                  >
                    <label>
                      Observação final
                      <textarea rows="3" value={finishNotes} onChange={(e) => setFinishNotes(e.target.value)} placeholder="Resumo da arbitragem, observação do fechamento ou incidente." />
                    </label>
                    <button className="primary-button danger-button" type="submit" disabled={matchBusy === 'finish' || selectedMatch.status === 'finished'}>
                      {matchBusy === 'finish' ? 'Encerrando...' : 'Encerrar partida'}
                    </button>
                  </form>
                </div>
              </div>

              <form
                className="admin-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  onRegisterEvent(selectedMatch.id, {
                    team_id: Number(eventForm.team_id),
                    jogador_id: eventForm.jogador_id ? Number(eventForm.jogador_id) : null,
                    assist_jogador_id: eventForm.assist_jogador_id ? Number(eventForm.assist_jogador_id) : null,
                    event_type: eventForm.event_type,
                    minute: Number(eventForm.minute),
                    description: eventForm.description || null,
                  }).then(() => setEventForm({ ...INITIAL_EVENT, team_id: String(selectedMatch.home_team_id || '') }));
                }}
              >
                <h3>Registrar evento</h3>
                <div className="form-row triple">
                  <label>
                    Time
                    <select value={eventForm.team_id} onChange={(e) => setEventForm({ ...eventForm, team_id: e.target.value, jogador_id: '', assist_jogador_id: '' })}>
                      {teamOptions.map((team) => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Tipo
                    <select value={eventForm.event_type} onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}>
                      <option value="goal">Gol</option>
                      <option value="penalty_scored">Pênalti convertido</option>
                      <option value="penalty_missed">Pênalti perdido</option>
                      <option value="own_goal">Gol contra</option>
                      <option value="yellow_card">Cartão amarelo</option>
                      <option value="red_card">Cartão vermelho</option>
                      <option value="substitution">Substituição</option>
                      <option value="injury">Lesão</option>
                      <option value="corner">Escanteio</option>
                      <option value="shot_on_target">Finalização no alvo</option>
                      <option value="shot_off_target">Finalização para fora</option>
                      <option value="foul_suffered">Falta sofrida</option>
                      <option value="foul_committed">Falta cometida</option>
                      <option value="interception">Interceptação</option>
                      <option value="steal">Roubada de bola</option>
                      <option value="var">VAR</option>
                      <option value="half_time">Intervalo</option>
                      <option value="end">Fim</option>
                    </select>
                  </label>
                  <label>
                    Minuto
                    <input type="number" min="1" max="130" value={eventForm.minute} onChange={(e) => setEventForm({ ...eventForm, minute: e.target.value })} required />
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    Jogador
                    <select value={eventForm.jogador_id} onChange={(e) => setEventForm({ ...eventForm, jogador_id: e.target.value })}>
                      <option value="">Sem jogador</option>
                      {filteredPlayers.map((player) => (
                        <option key={player.jogador_id} value={player.jogador_id}>{player.jogador_name}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Assistência
                    <select value={eventForm.assist_jogador_id} onChange={(e) => setEventForm({ ...eventForm, assist_jogador_id: e.target.value })}>
                      <option value="">Sem assistência</option>
                      {filteredPlayers.map((player) => (
                        <option key={player.jogador_id} value={player.jogador_id}>{player.jogador_name}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <label>
                  Descrição
                  <input value={eventForm.description} onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })} placeholder="Observação operacional do lance." />
                </label>
                <button className="primary-button" type="submit" disabled={matchBusy === 'event'}>
                  {matchBusy === 'event' ? 'Registrando...' : 'Salvar evento'}
                </button>
              </form>

              <SectionCard title="Timeline da partida" subtitle="Eventos registrados até agora.">
                <div className="timeline-list">
                  {(selectedMatch.events || []).length ? selectedMatch.events.map((event) => (
                    <article key={event.id} className="timeline-item">
                      <div className="timeline-item__minute">{event.minute}&rsquo;</div>
                      <div>
                        <strong>{eventLabel(event.event_type)} • {event.jogador_name || event.team_name || 'Partida'}</strong>
                        <p>{event.jogador_name || 'Sem jogador'}{event.assist_jogador_name ? ` • Assistência: ${event.assist_jogador_name}` : ''}</p>
                        {event.description ? <span>{event.description}</span> : null}
                      </div>
                    </article>
                  )) : <div className="empty-state">Nenhum evento registrado ainda.</div>}
                </div>
              </SectionCard>

              {(selectedMatch.officiating_team || []).length ? (
                <SectionCard title="Escala de arbitragem" subtitle="Equipe oficial vinculada a esta partida.">
                  <div className="timeline-list">
                    {selectedMatch.officiating_team.map((official) => (
                      <article key={official.id || official.role_code} className="timeline-item">
                        <div>
                          <strong>{(OFFICIATING_ROLES.find((item) => item.code === official.role_code)?.label) || official.role_code}</strong>
                          <p>{official.display_name || official.user_name || '-'}</p>
                          {official.notes ? <span>{official.notes}</span> : null}
                        </div>
                      </article>
                    ))}
                  </div>
                </SectionCard>
              ) : null}
            </div>
          ) : (
            <div className="empty-state">Selecione um jogo para abrir a central operacional.</div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}

